import { MessageBar, MessageBarType, Stack, Toggle } from '@fluentui/react';
import { Accordion, Dropdown, TextField } from '@h2oai/ui-kit';

import { AIEMOpType } from '../../../../aiem/engine/types';
import { DAIEngine } from '../../../../aiem/gen/ai/h2o/engine/v1/dai_engine_pb';
import { Engine_Type } from '../../../../aiem/gen/ai/h2o/engine/v1/engine_pb';
import { ConstraintType } from '../../../../aiem/types';
import { getIdFromName } from '../../../../aiem/utils';
import { useFormAttributes } from '../../../../utils/utils';
import { EditablePanelFooter } from '../../../EditablePanelFooter/EditablePanelFooter';
import { H2OEngineDefaultProfiles } from '../../constants';
import { AdvancedConfiguration } from '../AdvancedConfiguration/AdvancedConfiguration';
import { ConstraintInput } from '../ConstraintInput/ConstraintInput';
import { DisplayAndId } from '../DisplayAndId/DisplayAndId';
import ConfigureCustomEngineProfile from './components/ConfigureCustomEngineProfile';
import { useDAILicenseExpiration, useEngineConfiguration } from './hooks';

export type EngineConfigurationProps = {
  op: AIEMOpType;
  engine: any; // Using any temporarily to avoid type conflicts during refactor
  onSave: () => any;
  onCancel: () => any;
};

export function EngineConfiguration({ op, engine, onCancel, onSave }: EngineConfigurationProps) {
  const { inputContainerProps, inputRowProps, sliderContainerProps, sliderFormRowProps } = useFormAttributes();

  const {
    // State
    isLoading,
    engineState,

    // Refs
    advancedConfigurationRef,

    // Computed values
    isNotebookEngine,

    // Functions
    updateEngine,
    onClickSave,

    // Validation
    isEngineValid,
    isConfigEditabilityDisabled,
    validateAdvancedConfiguration,
    isNewOrUpdated,

    // Versions
    selectedVersion,
    versions,
    handleVersionChange,
    isVersionInList,

    // Profiles
    selectedProfileOption,
    profileOptions,
    selectedConfigurationOption,
    handleProfileOptionSelect,
    handleConfigurationOptionSelect,
    isProfileInList,
    isDAIEngine,
  } = useEngineConfiguration(op, engine);

  const onDisplayNameChange = (value: string) => updateEngine({ displayName: value });
  const onIdChange = (value: string) => updateEngine({ id: value });

  const handleSave = () => onClickSave(onSave, onCancel);

  const isCreate = op === AIEMOpType.create;
  const isEdit = op === AIEMOpType.edit;
  const isView = op === AIEMOpType.view;
  const isH2OEngine = engine?.engineType === Engine_Type.H2O;

  const { daysUntilExpiration, showWarning: showExpirationWarning } = useDAILicenseExpiration(isDAIEngine && isCreate);

  const showVersionBlock = isCreate || (isEdit && !isDAIEngine) || isView;
  const showVersionDropdown = isCreate || (isEdit && !isDAIEngine);
  const showH2ODatasetParams = isH2OEngine;

  return (
    <div style={{ paddingBottom: '50px' }}>
      <form>
        <Stack>
          <DisplayAndId
            engine={engine}
            onDisplayNameChange={onDisplayNameChange}
            onIdChange={onIdChange}
            editableId={isCreate}
          />

          {showExpirationWarning && (
            <MessageBar
              messageBarType={
                daysUntilExpiration !== null && daysUntilExpiration <= 0 ? MessageBarType.error : MessageBarType.warning
              }
              styles={{ root: { marginBottom: 16 } }}
            >
              {daysUntilExpiration !== null && daysUntilExpiration <= 0
                ? 'Driverless AI license has expired'
                : `Driverless AI license expires in ${daysUntilExpiration} day${daysUntilExpiration === 1 ? '' : 's'}`}
            </MessageBar>
          )}

          <Stack {...inputRowProps}>
            <Stack {...inputContainerProps}>
              <Dropdown
                label="Profile"
                selectedKey={selectedProfileOption?.name}
                options={profileOptions}
                onChange={(_, option) => handleProfileOptionSelect(undefined, option?.data)}
              />
            </Stack>
          </Stack>

          {!isLoading && profileOptions.length === 0 && (
            <Stack>
              <p style={{ margin: '-8px 0 20px', color: 'var(--h2o-red500, #ba2525)' }}>
                There are no available profiles to chose.
              </p>
            </Stack>
          )}

          {!isLoading && !isProfileInList && (
            <Stack>
              <p style={{ margin: '-8px 0 20px', color: 'var(--h2o-red500, #ba2525)' }}>
                Profile &quot;{getIdFromName(selectedProfileOption?.name)}&quot; is disabled or deleted. Please select a
                different profile.
              </p>
            </Stack>
          )}

          {selectedProfileOption && (
            <Stack>
              {showH2ODatasetParams && (
                <Stack {...inputRowProps}>
                  <Stack {...inputContainerProps}>
                    <Dropdown
                      label="Dataset Parameters"
                      selectedKey={selectedConfigurationOption?.key}
                      options={H2OEngineDefaultProfiles}
                      onChange={(_, option) => handleConfigurationOptionSelect(undefined, option)}
                    />
                  </Stack>
                </Stack>
              )}

              {showVersionBlock && (
                <Stack {...inputRowProps}>
                  <Stack {...inputContainerProps}>
                    {showVersionDropdown && (
                      <Dropdown
                        label={isNotebookEngine ? 'Image' : 'Version'}
                        placeholder={`Select a${isNotebookEngine ? 'n Image' : ' Version'}`}
                        options={versions}
                        selectedKey={selectedVersion}
                        onChange={handleVersionChange}
                      />
                    )}
                    {isView && (
                      <TextField
                        name="version"
                        label="Version"
                        value={engineState?.version}
                        onChange={() => null}
                        readOnly
                      />
                    )}
                  </Stack>
                </Stack>
              )}

              {!isLoading && versions.length > 0 && selectedVersion && !isVersionInList && (
                <Stack>
                  <p style={{ margin: '-8px 0 20px', color: 'var(--h2o-red500, #ba2525)' }}>
                    {isNotebookEngine ? 'Image' : 'Version'} &quot;{getIdFromName(selectedVersion)}&quot; is not
                    available. Please select a different {isNotebookEngine ? 'image' : 'version'}.
                  </p>
                </Stack>
              )}

              {!isLoading && versions.length === 0 && (
                <Stack>
                  <p style={{ margin: '-8px 0 20px', color: 'var(--h2o-red500, #ba2525)' }}>
                    There are no available {isNotebookEngine ? 'images' : 'versions'} to chose.
                  </p>
                </Stack>
              )}

              <ConfigureCustomEngineProfile
                selectedConfigurationOption={selectedConfigurationOption}
                engine={engineState}
                constraintSet={selectedProfileOption}
                modifyEngine={updateEngine}
                operationCreate={isCreate}
              />

              {isNotebookEngine && (
                <Stack
                  {...inputRowProps}
                  styles={{ ...inputRowProps.styles, root: { ...inputRowProps.styles.root, height: 40 } }}
                  verticalAlign="center"
                >
                  <Stack {...inputContainerProps}>
                    <Stack horizontal>
                      <Stack styles={{ root: { fontWeight: 600, padding: '0 3px', width: 160 } }}>Shared</Stack>
                      <Stack>
                        <Toggle
                          checked={!!engineState.shared}
                          onChange={(_ev, shared) => {
                            updateEngine({ shared });
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              )}

              <Accordion
                title="Timeout Configuration"
                isClose
                styles={{ title: { fontWeight: 400 }, root: { marginTop: 20 } }}
              >
                <Stack {...sliderFormRowProps} styles={{ root: { width: 400, height: 77 } }}>
                  <Stack {...sliderContainerProps}>
                    <ConstraintInput
                      constraintType={ConstraintType.MAXIDLEDURATION}
                      constraint={selectedProfileOption?.maxIdleDurationConstraint}
                      value={engineState?.[ConstraintType.MAXIDLEDURATION]}
                      onChange={(_event, value) => {
                        const fieldName = ConstraintType.MAXIDLEDURATION as string;
                        updateEngine({ [fieldName]: value });
                      }}
                      label="Max Idle Time (Hours)"
                      tooltip={
                        isH2OEngine
                          ? 'The engine will be terminated after being idle for more than the Max Idle Time.'
                          : 'The engine will be paused after being idle for more than the Max Idle Time.'
                      }
                    />
                  </Stack>
                </Stack>
                <Stack {...sliderFormRowProps} styles={{ root: { width: 400, height: 77 } }}>
                  <Stack {...sliderContainerProps}>
                    <ConstraintInput
                      label="Max Up Time (Hours)"
                      constraintType={ConstraintType.MAXRUNNINGDURATION}
                      constraint={selectedProfileOption?.maxRunningDurationConstraint}
                      value={engineState?.[ConstraintType.MAXRUNNINGDURATION]}
                      onChange={(_event, value) => {
                        const fieldName = ConstraintType.MAXRUNNINGDURATION as string;
                        updateEngine({ [fieldName]: value });
                      }}
                      tooltip={
                        isH2OEngine
                          ? 'The engine will be terminated after running for more than the Max Up Time.'
                          : 'The engine will be paused after running for more than the Max Up Time.'
                      }
                    />
                  </Stack>
                </Stack>
              </Accordion>

              {isDAIEngine && !isConfigEditabilityDisabled && (
                <Accordion
                  title="Advanced Configuration (config.toml)"
                  styles={{
                    title: { fontWeight: 400 },
                    root: { marginTop: 12, marginBottom: 40, height: 'unset !important' },
                  }}
                >
                  <AdvancedConfiguration
                    validation={validateAdvancedConfiguration}
                    editOnly={false} // This would need to be computed based on profile
                    ref={advancedConfigurationRef}
                    config={(engineState as DAIEngine).config || {}}
                    onConfigChange={(updatedConfig) => {
                      updateEngine({ config: updatedConfig });
                    }}
                  />
                </Accordion>
              )}
            </Stack>
          )}
        </Stack>
      </form>

      <EditablePanelFooter
        onCancel={onCancel}
        onSave={handleSave}
        closeButtonText={isCreate ? 'Back' : 'Close'}
        saveButtonText={isCreate ? 'Create' : 'Save'}
        saveButtonDisabled={!selectedProfileOption || !isEngineValid || !isNewOrUpdated()}
      />
    </div>
  );
}
